import {useCallback, useEffect, useMemo, useState} from 'react';
import {
  QueryFunctionContext,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';
import {format} from 'date-fns';
import {
  ListResponse,
  Order,
  Vehicle,
  RouteStatus,
  Driver,
  Address,
  createAddress,
} from 'types';
import api from 'api';
import {useHistory, useParams} from 'react-router-dom';
import {concat, get, sortBy as sortLodash} from 'lodash';
import {DropResult} from 'react-beautiful-dnd';
import toast from 'react-hot-toast';
import useOrderList from 'hooks/useOrderList';

export interface Route {
  id: number | string;
  driver?: Driver;
  vehicle?: Vehicle;
  start_time: string | undefined;
  start_location: string;
  end_location: string;
  orders: Order[];
  deliveries: Order[];
  canceled_deliveries: Order[];
  status: RouteStatus;
  total_time?: number;
  isClear?: boolean;
  start_latitude: number;
  start_longitude: number;
  end_latitude: number;
  end_longitude: number;
}
export interface DriverRoute {
  id: number;
  note: string;
}

function roundToNearest30Minutes() {
  const roundedDate = new Date();
  const minutes = roundedDate.getMinutes();

  // Calculate the difference to the next 15-minute interval
  const remainder = minutes % 30;
  const difference = 30 - remainder;

  // Add the difference to the current time
  roundedDate.setMinutes(minutes + difference);

  return roundedDate;
}

const roundedTime = roundToNearest30Minutes();

let selectedTime = roundedTime.toISOString();

const defaultRoute: Route = {
  id: '0',
  start_time:
    format(Date.now(), 'yyyy-MM-dd') + selectedTime.slice(10, 19) + 'Z',
  start_location: '',
  end_location: '',
  orders: [],
  deliveries: [],
  canceled_deliveries: [],
  status: 'new',
  start_latitude: 40.7596958,
  start_longitude: 73.9650045,
  end_latitude: 40.7596958,
  end_longitude: 73.9650045,
};

function getRequestData(route: Route) {
  return {
    ...route,
    driver: route.vehicle?.driver?.id,
    vehicle: route.vehicle?.id,
    orders: [],
    deliveries: route.deliveries.map((o) => o.id),
    // deliveries: route.orders.map((o) => o.id),
  };
}

async function getVehicles() {
  const {data} = await api.get<ListResponse<Vehicle>>('/vehicles/');
  return data;
}

async function getRouteById({queryKey}: QueryFunctionContext) {
  const [, routeId] = queryKey;
  if (routeId) {
    const {data} = await api.get<Route>(`/routes/${routeId}/`);
    return data;
  }

  return defaultRoute;
}
async function getCustomerAddresses({queryKey}: QueryFunctionContext) {
  const [, customerId] = queryKey;
  if (!customerId) return;
  const {data} = await api.get<ListResponse<Address>>(`/customers/address/`, {
    params: {customer: customerId, limit: 100},
  });
  return data?.results;
}

function useRouteHistory() {
  const {goBack} = useHistory();
  const {id} = useParams<{id: string}>();

  const routeId = useMemo(() => id, [id]);
  const [date, setDate] = useState<undefined>(undefined);
  const [activeOrder, setActiveOrder] = useState<Order | undefined>();
  const [newAddressModal, setNewAddressModal] = useState(false);
  const [defaultCheckedAddressId, setDefaultCheckedAddressId] = useState(0);
  const [changingPositions, setChangingPositions] = useState<boolean>(false);
  const [isAddingNewDeliveries, setIsAddingNewDeliveries] =
    useState<boolean>(false);
  const [deliveries, setDeliveries] = useState<Order[]>([]);
  const queryClient = useQueryClient();
  const {data: vehicles} = useQuery('vehicles', getVehicles);
  const {orders: inboxOrders, refetch} = useOrderList({
    unAssignedOnly: true,
    sortBy: 'most_recent',
  });
  let {data: route, isFetching} = useQuery(['route', routeId], getRouteById, {
    initialData: defaultRoute,
    refetchOnWindowFocus: Boolean(routeId),
    refetchInterval: 0,
  });

  const {data: customerAddresses, refetch: refetchCustomerAddresses} = useQuery(
    ['customer_addresses', activeOrder?.customer?.id],
    getCustomerAddresses,
    {
      refetchOnWindowFocus: false,
      enabled: !!activeOrder,
    }
  );
  useEffect(() => {
    setDeliveries(
      sortLodash(
        concat(
          ...(route?.deliveries || []),
          ...(route?.canceled_deliveries.map((item) => ({
            ...item,
            is_canceled: true,
          })) || [])
        ),
        'number'
      )
    );
  }, [route]);

  const onChangeData = useCallback((value: any) => {
    setDate(value);
  }, []);

  const updateRoute = useMutation(
    'update_route',
    async function (nextRoute: Route) {
      const nextRouteData = getRequestData({
        ...route,
        ...nextRoute,
      });

      // save changes if route is already created
      if (route?.id && !nextRoute?.isClear) {
        const {data} = await api.patch<Route>(
          `/routes/${route?.id}/`,
          nextRouteData
        );
        return data;
      }

      // just return changes if route is not created
      return {
        ...route,
        ...nextRoute,
      };
    },

    {
      onMutate: async (nextRoute) => {
        await queryClient.cancelQueries(['route', routeId]);

        const previousRoute = queryClient.getQueryData<Route>([
          'routes',
          routeId,
        ]);

        queryClient.setQueryData(['route', routeId], {
          ...route,
          ...nextRoute,
        });

        return {previousRoute};
      },
      onSettled: (data) => {
        if (!data) {
          queryClient.invalidateQueries(['route', routeId]);
        }
      },
    }
  );

  const [selectedAssignedOrders, setSelectedAssignedOrders] = useState<
    number[]
  >([]);

  const updatePositions = useCallback(
    async (orders: Order[]) => {
      setChangingPositions(true);
      try {
        const {data} = await api.post(
          `/routes/routes/${route?.id}/update-delivery-positions/`,
          {
            deliveries: orders.map((item, index) => ({
              id: item.id,
              delivery_position: index + 1,
              delivery_status: item.status,
            })),
          }
        );
        updateRoute.mutate({...data, isClear: true});
        refetch();
      } catch (error) {
        const errors = get(error, 'response,data', {});
        const error_arr = Object.values(errors);
        toast.error(
          (get(error_arr, '[0]', '') as string) || 'Something went wrong'
        );
      } finally {
        setChangingPositions(false);
      }
    },
    [refetch, route?.id, updateRoute]
  );

  const handleDeleteOrder = useCallback(
    async (id: number) => {
      await api.post(`/routes/deliveries/${id}/cancel/`);
      if (deliveries.length === 1 || route?.deliveries.length === 1) {
        goBack();
        return;
      }

      updatePositions(deliveries);
    },
    [goBack, deliveries, updatePositions, route?.deliveries.length]
  );

  const handleDeleteAll = useCallback(async () => {
    if (!route) return;
    await api.post(`/routes/deliveries/${route.id}/cancel-all/`);
    goBack();
  }, [route, goBack]);
  const addNewDeliveries = useCallback(
    async (ids: number[]) => {
      if (!route) return;
      try {
        setIsAddingNewDeliveries(true);
        const res = await api.post(`/routes/${route?.id}/add-new-deliveries/`, {
          delivery_ids: ids,
        });
        if (res.status === 200) {
          updatePositions(deliveries);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setIsAddingNewDeliveries(false);
      }
    },
    [deliveries, route, updatePositions]
  );
  const reverseSequence = (closeModal: () => void) => {
    const newArray = [...deliveries];
    const reversedDeliveries = newArray.reverse();
    try {
      updatePositions(reversedDeliveries);
    } catch (err) {
      console.log(err);
    } finally {
      closeModal();
    }
  };
  const onChangeAddress = async (
    deliveryId: number,
    addressId: number,
    callback: () => void
  ) => {
    try {
      await api.post(`/routes/${deliveryId}/change-delivery-address/`, {
        address_id: addressId,
      });
      updatePositions(deliveries);
      callback();
    } catch (error) {
      console.log(error);
    }
  };

  async function createNewAddress(
    newAddress: createAddress,
    callback: () => void
  ) {
    try {
      const data = await api.post<Address>(`/customers/address/`, {
        customer_id: Number(activeOrder?.customer?.id),
        ...newAddress,
      });
      if (data.status === 200) {
        refetchCustomerAddresses();
        callback();
        setDefaultCheckedAddressId(data?.data?.id);
      }
    } catch (error) {
      toast.error('Failed to create a new address!');
    }
  }

  function onChangeAssignedCheckbox(id: number, checked: Boolean) {
    if (checked) {
      setSelectedAssignedOrders([...selectedAssignedOrders, id]);
    } else {
      setSelectedAssignedOrders(
        selectedAssignedOrders.filter((selected) => selected !== id)
      );
    }
  }

  async function sendDriverNote(driverNote: DriverRoute) {
    return await api.post(`/routes/deliveries/staff_note/${driverNote.id}/`, {
      staff_note: driverNote.note,
    });
  }

  const reorder = (list: Order[], startIndex: number, endIndex: number) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
  };

  const handleDragEnd = async (result: DropResult) => {
    const status = route?.deliveries?.[result.source.index]?.status;
    if (
      !result.destination ||
      route?.status !== 'awaiting' ||
      status === 'completed'
    )
      return;

    const res = reorder(
      deliveries,
      result.source.index,
      result.destination.index
    );
    setDeliveries(res);
    updatePositions(res);
  };

  return {
    route: route ? route : defaultRoute,
    currentVehicle: vehicles?.results.find((v) => v.id === route?.vehicle?.id),
    inboxOrders,
    vehicles,
    activeOrder,
    customerAddressSelectOptions: customerAddresses,
    setActiveOrder,
    onChangeAssignedCheckbox,
    onChangeData,
    onChangeAddress,
    createNewAddress,
    sendDriverNote,
    date,
    roundedTime,
    handleDeleteOrder,
    handleDeleteAll,
    handleDragEnd,
    addNewDeliveries,
    newAddressModal,
    setNewAddressModal,
    defaultCheckedAddressId,
    setDefaultCheckedAddressId,
    deliveries,
    changingPositions,
    isFetching,
    isAddingNewDeliveries,
    reverseSequence,
  };
}

export default useRouteHistory;
